import { Component, effect, inject, input } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent, FormTextareaComponent, FormToggleSwitchComponent } from '@shared/forms';
import { AssessmentFacade } from '@app/store/assessment';

@Component({
    selector: 'app-course-assessment',
    templateUrl: './assessment.component.html',
    styleUrls: ['./assessment.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, FormTextareaComponent, FormToggleSwitchComponent],
})
export class AssessmentComponent {
    private readonly assessmentFacade = inject(AssessmentFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly translateService = inject(TranslateService);

    readonly courseId = input.required<string>();

    readonly assessment = this.assessmentFacade.assessment;
    readonly saving = this.assessmentFacade.saving;

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        description: this.formBuilder.control(''),
        showToAssessor: this.formBuilder.control(true),
    });

    constructor() {
        effect(() => {
            this.assessmentFacade.getAssessment(this.courseId());
        });

        effect(() => {
            const assessment = this.assessment();
            this.form.reset({
                name: assessment?.name ?? '',
                description: assessment?.description ?? '',
                showToAssessor: assessment?.showToAssessor ?? true,
            });
        });
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            const formValue = this.form.getRawValue();
            const assessment = this.assessment();

            if (assessment) {
                this.assessmentFacade.patchAssessment(assessment.id, formValue);
            } else {
                this.assessmentFacade.saveAssessment(this.courseId(), formValue);
            }
        }
    }

    onDelete(): void {
        const assessment = this.assessment();
        if (!assessment) {
            return;
        }

        this.confirmationService.confirm({
            header: this.translateService.instant('assessment.delete.header'),
            message: this.translateService.instant('assessment.delete.message'),
            icon: 'pi pi-exclamation-triangle',
            rejectButtonProps: {
                label: this.translateService.instant('assessment.delete.reject'),
                severity: 'secondary',
                outlined: true,
            },
            acceptButtonProps: {
                label: this.translateService.instant('assessment.delete.accept'),
                severity: 'danger',
            },
            accept: () => {
                this.assessmentFacade.deleteAssessment(assessment.id);
            },
        });
    }
}
